/**
 * Device Detection Constants
 *
 * Named constants for device detection system to eliminate magic strings.
 * All device detection comparisons MUST use these constants.
 *
 * @see src/utils/deviceDetection.ts
 * @see src/hooks/useResponsive.ts
 */

// ============================================================================
// Layout Mode Constants
// ============================================================================

/**
 * Mobile layout mode - used for phones and tablets in portrait orientation
 * @constant
 */
export const LAYOUT_MODE_MOBILE = 'mobile' as const;

/**
 * Desktop layout mode - used for tablets in landscape and desktop devices
 * @constant
 */
export const LAYOUT_MODE_DESKTOP = 'desktop' as const;

/**
 * Layout mode type - determines UI structure
 */
export type LayoutMode = typeof LAYOUT_MODE_MOBILE | typeof LAYOUT_MODE_DESKTOP;

// ============================================================================
// Input Mode Constants
// ============================================================================

/**
 * Touch input mode - device has touch capability as primary input
 * @constant
 */
export const INPUT_MODE_TOUCH = 'touch' as const;

/**
 * Mouse input mode - device uses mouse/trackpad as primary input
 * @constant
 */
export const INPUT_MODE_MOUSE = 'mouse' as const;

/**
 * Input mode type - determines control type (touch gestures vs mouse interactions)
 */
export type InputMode = typeof INPUT_MODE_TOUCH | typeof INPUT_MODE_MOUSE;

// ============================================================================
// Orientation Constants
// ============================================================================

/**
 * Portrait orientation - height > width
 * @constant
 */
export const ORIENTATION_PORTRAIT = 'portrait' as const;

/**
 * Landscape orientation - width >= height
 * @constant
 */
export const ORIENTATION_LANDSCAPE = 'landscape' as const;

/**
 * Orientation type
 */
export type Orientation =
  | typeof ORIENTATION_PORTRAIT
  | typeof ORIENTATION_LANDSCAPE;

// ============================================================================
// Pointer Type Constants
// ============================================================================

/**
 * Coarse pointer - touch input (less precise)
 * @constant
 */
export const POINTER_COARSE = 'coarse' as const;

/**
 * Fine pointer - mouse/trackpad input (precise)
 * @constant
 */
export const POINTER_FINE = 'fine' as const;

/**
 * Pointer type - indicates precision of primary pointing device
 */
export type Pointer = typeof POINTER_COARSE | typeof POINTER_FINE;
